/**
 * The questions a reviewer answers in prose, and the keys they are stored
 * under — DM-WF-001 EPIC-063.
 *
 * Fixed wording rather than per-relationship configuration: a blank box after
 * a page of ratings is where most people write "n/a", and one question asked
 * of everyone gives HR a set of answers that can be read side by side. Making
 * the wording editable per reviewer type is EPIC-080's shape of problem and can
 * come later without moving any stored data, because `review_feedback` records
 * the prompt KEY and not its text.
 */

export const FEEDBACK_PROMPTS = {
  /** The tagged notes. Attributed, and editable until the round closes. */
  general: "What should this person keep doing, and what should they change?",
  /**
   * The one unattributed comment a reviewer may leave.
   *
   * Written before handing in and committed with it, because anonymity rules
   * out the alternatives: there is no author to look the row up by afterwards,
   * so it can be neither edited nor counted — and a "they left one" flag on
   * `review_reviewer` would be the de-anonymisation the whole design avoids.
   * Hand-in happens once, so one-per-reviewer follows from that alone.
   */
  anonymous: "Anything you would rather say without your name on it?",
  /**
   * Standalone feedback given outside any review cycle — EPIC-067 WI-243.
   * Open text, no fixed question: the giver just says what they came to say.
   */
  ad_hoc: "What would you like to say about this person?",
  /**
   * Quick feedback tied to a specific project — EPIC-068 WI-250. Same
   * standalone shape as `ad_hoc`, distinguished by carrying a project tag
   * (`related_nos_project_id` / `related_project_name`) rather than by a
   * different prompt — deliberately short-form, not a full competency set
   * (AC-717).
   */
  post_project: "What would you like to say about working with them on this project?",
} as const;

export type FeedbackPromptKey = keyof typeof FEEDBACK_PROMPTS;
